import React from 'react';

/**
 * Renderizador de markdown leve, sem dependências externas.
 * Cobre o que o agente vai gerar: H1-H3, parágrafos, listas, bold/italic,
 * links, blockquotes, hr, código inline.
 *
 * Se no futuro precisar de tabelas, code blocks, ou GFM mais completo,
 * trocar esse componente por `react-markdown` (npm install react-markdown).
 */

const escapeHtml = (s) =>
  s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const slugify = (s) =>
  s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

// processa marcações inline (bold, italic, code, link) — recebe texto já escapado
const processInline = (text) => {
  let out = text;
  // links [texto](url)
  out = out.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
  );
  // bold **texto** ou __texto__
  out = out.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  out = out.replace(/__([^_]+)__/g, '<strong>$1</strong>');
  // italic *texto* ou _texto_
  out = out.replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, '$1<em>$2</em>');
  out = out.replace(/(^|[^_])_([^_\n]+)_(?!_)/g, '$1<em>$2</em>');
  // code inline `texto`
  out = out.replace(/`([^`]+)`/g, '<code>$1</code>');
  return out;
};

// converte um bloco de markdown em HTML
const markdownToHtml = (md) => {
  if (!md) return '';
  const lines = md.split('\n');
  const html = [];
  let i = 0;

  // Pula H1 de abertura — o título do post já é renderizado pelo PostTitle
  // do BlogPostPage, então se o conteúdo começa com "# Título" fica duplicado.
  while (i < lines.length && !lines[i].trim()) i++;
  if (i < lines.length && /^#\s+/.test(lines[i])) i++;

  while (i < lines.length) {
    const line = lines[i];

    // linha vazia
    if (!line.trim()) {
      i++;
      continue;
    }

    // hr
    if (/^---+$/.test(line.trim())) {
      html.push('<hr/>');
      i++;
      continue;
    }

    // headings
    const h1 = line.match(/^#\s+(.+)$/);
    if (h1) {
      const text = processInline(escapeHtml(h1[1]));
      const id = slugify(h1[1]);
      html.push(`<h1 id="${id}">${text}</h1>`);
      i++;
      continue;
    }
    const h2 = line.match(/^##\s+(.+)$/);
    if (h2) {
      const text = processInline(escapeHtml(h2[1]));
      const id = slugify(h2[1]);
      html.push(`<h2 id="${id}">${text}</h2>`);
      i++;
      continue;
    }
    const h3 = line.match(/^###\s+(.+)$/);
    if (h3) {
      const text = processInline(escapeHtml(h3[1]));
      const id = slugify(h3[1]);
      html.push(`<h3 id="${id}">${text}</h3>`);
      i++;
      continue;
    }

    // blockquote (consome linhas consecutivas)
    if (line.startsWith('> ')) {
      const buf = [];
      while (i < lines.length && lines[i].startsWith('> ')) {
        buf.push(lines[i].slice(2));
        i++;
      }
      html.push(`<blockquote><p>${processInline(escapeHtml(buf.join(' ')))}</p></blockquote>`);
      continue;
    }

    // unordered list
    if (/^[-*]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i])) {
        const item = lines[i].replace(/^[-*]\s+/, '');
        items.push(`<li>${processInline(escapeHtml(item))}</li>`);
        i++;
      }
      html.push(`<ul>${items.join('')}</ul>`);
      continue;
    }

    // ordered list
    if (/^\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i])) {
        const item = lines[i].replace(/^\d+\.\s+/, '');
        items.push(`<li>${processInline(escapeHtml(item))}</li>`);
        i++;
      }
      html.push(`<ol>${items.join('')}</ol>`);
      continue;
    }

    // imagem ![alt](src)
    const img = line.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
    if (img) {
      html.push(`<img src="${img[2]}" alt="${escapeHtml(img[1])}" loading="lazy"/>`);
      i++;
      continue;
    }

    // parágrafo (consome linhas consecutivas até linha vazia ou outro bloco)
    const paraLines = [];
    while (
      i < lines.length &&
      lines[i].trim() &&
      !/^#{1,6}\s/.test(lines[i]) &&
      !/^[-*]\s+/.test(lines[i]) &&
      !/^\d+\.\s+/.test(lines[i]) &&
      !lines[i].startsWith('> ') &&
      !/^---+$/.test(lines[i].trim())
    ) {
      paraLines.push(lines[i]);
      i++;
    }
    if (paraLines.length) {
      html.push(`<p>${processInline(escapeHtml(paraLines.join(' ')))}</p>`);
    }
  }

  return html.join('\n');
};

export default function MarkdownRenderer({ children }) {
  const html = markdownToHtml(children || '');
  // eslint-disable-next-line react/no-danger
  return <div dangerouslySetInnerHTML={{ __html: html }} />;
}
